import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { authApi } from '@/services/authApi';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { PATHS } from '@/constants/paths';
import M_LanguageSwitcher from '@/components/M_Molecules/M_LanguageSwitcher/M_LanguageSwitcher';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import styles from './AuthPage.module.css';
import cn from 'classnames';
import AuthImage from '@/assets/images/A_ScreenSaver.png';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);

  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setError(null);
    if (!newPassword.trim()) {
      setPasswordError(getTranslation(language, 'validation.passwordRequired'));
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(
        getTranslation(language, 'validation.passwordMinLength')
      );
      return;
    }
    if (!token) {
      setError(t('auth.resetPasswordNoToken'));
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.passwordResetConfirm(token, newPassword);
      if (res.success) {
        setSuccess(true);
        setMessage(res.data?.detail || t('auth.resetPasswordSuccess'));
      } else {
        setError(res.message || t('auth.resetPasswordError'));
      }
    } catch {
      setError(t('auth.resetPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.action_wrapper}>
          <M_LanguageSwitcher />
          <div className={styles.sign_section}>
            <div className={styles.title_block}>
              <h1>{t('auth.resetPasswordTitle')}</h1>
            </div>
            <div className={styles.form_section}>
              <A_Notification
                type='error'
                message={t('auth.resetPasswordNoToken')}
                isVisible
                onClose={() => {}}
              />
              <Link
                to={PATHS.FORGOT_PASSWORD}
                className={cn(styles.link_muted, 'text-p-5')}
              >
                {t('auth.forgotPasswordTitle')}
              </Link>
              <Link
                to={PATHS.AUTH}
                className={cn(styles.link_muted, 'text-p-5')}
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
        <div className={styles.image_wrapper}>
          <div className={styles.round_decorator} />
          <img
            src={AuthImage}
            alt='Карусель с фотографиями'
            className={styles.auth_image}
          />
          <span className={cn(styles.copyright_text, 'text-p-6')}>
            2026 © Timecaps
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.action_wrapper}>
        <M_LanguageSwitcher />
        <div className={styles.sign_section}>
          <div className={styles.title_block}>
            <h1>{t('auth.resetPasswordTitle')}</h1>
          </div>
          <div className={styles.form_section}>
            {success ? (
              <>
                {message && (
                  <A_Notification
                    type='success'
                    message={message}
                    isVisible
                    onClose={() => setMessage(null)}
                  />
                )}
                <Link
                  to={PATHS.AUTH}
                  className={cn(styles.link_muted, 'text-p-5')}
                >
                  {t('auth.backToLogin')}
                </Link>
              </>
            ) : (
              <>
                <form onSubmit={handleSubmit} className={styles.form_inner}>
                  <div className={styles.inputs_row}>
                    <div className={styles.inputs_group}>
                      <A_Input
                        label={t('auth.newPasswordLabel')}
                        type='password'
                        value={newPassword}
                        onChange={v => {
                          setNewPassword(v);
                          if (passwordError) setPasswordError('');
                        }}
                        required
                        error={passwordError}
                      />
                    </div>
                  </div>
                  <div className={styles.button_row}>
                    <A_Button
                      type='submit'
                      variant='primary'
                      size='large'
                      disabled={loading}
                    >
                      {t('auth.resetPassword')}
                    </A_Button>
                  </div>
                </form>
                {error && (
                  <A_Notification
                    type='error'
                    message={error}
                    isVisible
                    onClose={() => setError(null)}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className={styles.image_wrapper}>
        <div className={styles.round_decorator} />
        <img
          src={AuthImage}
          alt='Карусель с фотографиями'
          className={styles.auth_image}
        />
        <span className={cn(styles.copyright_text, 'text-p-6')}>
          2026 © Timecaps
        </span>
      </div>
    </div>
  );
}
